/**
 * iter1040 — BHI Hero Contextual Strips.
 *
 * Renders up to 4 chips in the hero:
 *   1. Social proof   — anonymised platform-wide user count
 *   2. BC subtype     — molecular subtype chip when declared (Luminal A / TNBC…)
 *   3. Prevention Prog — month N/12 progress bar with %
 *   4. Top-3 Twins    — quick jump to highest-impact Digital Twins
 *
 * All fetched from a single `/api/bhi/hero-strips` endpoint.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  Users, Sparkles, Target, Users2, ChevronRight, Dna, Brain,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHIP_BASE = "inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-semibold border transition";

export default function BhiHeroStrips({ isDark = false }) {
  const [strips, setStrips] = useState(null);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/bhi/hero-strips`)
      .then(({ data }) => { if (alive) setStrips(data); })
      .catch(() => { if (alive) setStrips({}); });
    return () => { alive = false; };
  }, []);

  if (!strips) return null;

  const chips = [];

  // Social proof
  if (strips.social_proof?.total_users >= 3) {
    chips.push(
      <div
        key="social"
        data-testid="bhi-hero-social-proof"
        className={`${CHIP_BASE} ${
          isDark ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
                 : "border-emerald-200 bg-emerald-50 text-emerald-700"
        }`}
        title="Активни потребители на платформата"
      >
        <Users className="w-3.5 h-3.5" />
        <span>{strips.social_proof.label_short_bg}</span>
      </div>
    );
  }

  // BC subtype chip
  if (strips.bc_subtype?.declared) {
    chips.push(
      <Link
        key="subtype"
        to="/health-intelligence/breast#section-bc-subtype"
        data-testid="bhi-hero-bc-subtype"
        className={`${CHIP_BASE} ${
          isDark ? "border-fuchsia-500/40 bg-fuchsia-500/10 text-fuchsia-300 hover:bg-fuchsia-500/20"
                 : "border-fuchsia-300 bg-fuchsia-50 text-fuchsia-700 hover:bg-fuchsia-100"
        }`}
        title="Твоят молекулярен подтип"
      >
        <Sparkles className="w-3.5 h-3.5" />
        <span>Подтип: {strips.bc_subtype.label_bg}</span>
      </Link>
    );
  }

  // Prevention Program month/12 progress
  if (strips.prevention_program?.enrolled) {
    const pp = strips.prevention_program;
    chips.push(
      <Link
        key="prevention"
        to="/health-intelligence/breast#section-prevention-program"
        data-testid="bhi-hero-prevention-program"
        className={`${CHIP_BASE} ${
          isDark ? "border-teal-500/40 bg-teal-500/10 text-teal-300 hover:bg-teal-500/20"
                 : "border-teal-300 bg-teal-50 text-teal-700 hover:bg-teal-100"
        }`}
        title={`12-месечна Prevention Program · започнала ${pp.started_at || ""}`}
      >
        <Target className="w-3.5 h-3.5" />
        <span>Prevention: Месец {pp.month}/{pp.total_months}</span>
        <span className={`ml-1 relative h-1 w-10 rounded-full overflow-hidden ${isDark ? "bg-teal-500/25" : "bg-teal-200"}`}>
          <span
            className="absolute inset-y-0 left-0 rounded-full bg-teal-500"
            style={{ width: `${Math.min(pp.percent || 0, 100)}%` }}
          />
        </span>
      </Link>
    );
  }

  // Top-3 Twins (aggregated chip)
  if (strips.top_twins?.length) {
    chips.push(
      <Link
        key="twins"
        to="/dashboard/twins"
        data-testid="bhi-hero-top-twins"
        className={`${CHIP_BASE} ${
          isDark ? "border-indigo-500/40 bg-indigo-500/10 text-indigo-300 hover:bg-indigo-500/20"
                 : "border-indigo-300 bg-indigo-50 text-indigo-700 hover:bg-indigo-100"
        }`}
        title="Твоите топ-3 близнака с най-голямо влияние върху BHI"
      >
        <Users2 className="w-3.5 h-3.5" />
        <span>Топ-3 Twins:</span>
        <span className="opacity-80">
          {strips.top_twins.slice(0, 3).map((t) => t.name_bg.split(" ")[0]).join(" · ")}
        </span>
        <ChevronRight className="w-3 h-3 opacity-70" />
      </Link>
    );
  }

  // iter1040 — Genetics overlay (BRCA/PALB2/CHEK2/ATM)
  if (strips.genetics_overlay?.positive) {
    const g = strips.genetics_overlay;
    chips.push(
      <Link
        key="genetics"
        to="/health-intelligence/breast#section-hereditary"
        data-testid="bhi-hero-genetics-overlay"
        className={`${CHIP_BASE} ${
          isDark ? "border-rose-500/40 bg-rose-500/10 text-rose-300 hover:bg-rose-500/20"
                 : "border-rose-300 bg-rose-50 text-rose-700 hover:bg-rose-100"
        }`}
        title={`${g.strongest_gene}+ повишава риска ${g.strongest_multiplier}× · pathway: ${g.affected_pathway_bg}`}
      >
        <Dna className="w-3.5 h-3.5" />
        <span>{g.strongest_gene}+ · {g.strongest_multiplier}× {g.affected_pathway_bg}</span>
        <ChevronRight className="w-3 h-3 opacity-70" />
      </Link>
    );
  }

  // iter1044 — Mind & Body → BHI adjust (stress management → cortisol → BHI)
  if (strips.mindbody_adjust) {
    const m = strips.mindbody_adjust;
    const positive = m.adjust_pct > 0;
    const toneClasses = positive
      ? (isDark ? "border-lime-500/40 bg-lime-500/10 text-lime-300 hover:bg-lime-500/20"
                : "border-lime-300 bg-lime-50 text-lime-700 hover:bg-lime-100")
      : (isDark ? "border-amber-500/40 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20"
                : "border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100");
    chips.push(
      <Link
        key="mindbody"
        to="/dashboard/mindbody-lab"
        data-testid="bhi-hero-mindbody-adjust"
        className={`${CHIP_BASE} ${toneClasses}`}
        title={`${m.minutes_7d} мин / ${m.sessions_7d} сесии за последните 7 дни · cortisol → BHI мост`}
      >
        <Brain className="w-3.5 h-3.5" />
        <span>{m.label_bg}</span>
        <ChevronRight className="w-3 h-3 opacity-70" />
      </Link>
    );
  }

  if (!chips.length) return null;

  return (
    <div
      className="mt-3 flex flex-wrap items-center gap-2"
      data-testid="bhi-hero-strips"
    >
      {chips}
    </div>
  );
}
